import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Box, Text, Alert, Loader, Group, ActionIcon } from '@mantine/core';
import { IconSearch, IconAlertCircle } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { EntityBrowserDialog } from '../dialogs';
import { useSetList } from '../../hooks/useSetList';

interface SetSelectorProps {
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    description?: React.ReactNode;
    showOnlyValidTargets?: boolean;
    allowClear?: boolean;
}

export const SetSelector: React.FC<SetSelectorProps> = ({
    value,
    onChange,
    label = "Set",
    required = false,
    disabled = false,
    error = false,
    helperText,
    description,
    showOnlyValidTargets = false,
    allowClear = true
}) => {
    const { sets, loading, error: errorState } = useSetList(showOnlyValidTargets);
    const [browserOpen, setBrowserOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState<string>('');
    const [fetchingSelected, setFetchingSelected] = useState<boolean>(false);
    const [fetchedForId, setFetchedForId] = useState<string | null>(null);

    const failedSetsRef = useRef<Set<string>>(new Set());
    const attemptedFetchesRef = useRef<Set<string>>(new Set());

    const browserFilterFunction = useMemo(() => {
        if (!showOnlyValidTargets) return undefined;
        return (set: any) => {
            const hasFilterScope = set.FilterScope && set.FilterScope.trim() !== '';
            return hasFilterScope;
        };
    }, [showOnlyValidTargets]);

    useEffect(() => {
        const syncSelectedLabel = async () => {
            if (!value) {
                setSelectedLabel('');
                setFetchedForId(null);
                failedSetsRef.current.clear();
                attemptedFetchesRef.current.clear();
                return;
            }
            const present = sets.find(s => s.Id === value);
            if (present) {
                setSelectedLabel(present.DisplayName || present.Id || String(value));
                setFetchedForId(value);
                failedSetsRef.current.delete(value);
                attemptedFetchesRef.current.delete(value);
                return;
            }
            if (attemptedFetchesRef.current.has(value)) {
                if (failedSetsRef.current.has(value)) {
                    return;
                }
                if (!fetchingSelected) {
                    return;
                }
            }
            if (fetchingSelected) return;
            attemptedFetchesRef.current.add(value);
            try {
                setFetchingSelected(true);
                const full = await entityService.getEntityById(value, 'Set', true, ['Id', 'DisplayName']);
                setSelectedLabel(full?.DisplayName || String(value));
                setFetchedForId(value);
                failedSetsRef.current.delete(value);
            } catch (error: any) {
                setSelectedLabel(String(value));
                const statusCode = error?.response?.status || error?.status;
                const isNotFound = statusCode === 404 || error?.message?.toLowerCase().includes('not found');
                if (isNotFound) {
                    failedSetsRef.current.add(value);
                    setFetchedForId(value);
                }
            } finally {
                setFetchingSelected(false);
            }
        };
        syncSelectedLabel();
    }, [value, sets, entityService]);

    // Unified: Accepts both [{ Id }] and string[] for backward compatibility
    const handleBrowserConfirm = (input: Array<{ Id: string; DisplayName: string; EntityType: string }> | string[]) => {
        const id = Array.isArray(input) && input.length > 0
            ? (typeof input[0] === 'object' && input[0] !== null && 'Id' in input[0] ? (input[0] as { Id: string }).Id : (input[0] as string))
            : null;
        if (id) {
            onChange(id);
        }
        setBrowserOpen(false);
    };

    const selectData = useMemo(() => {
        const options = sets.map((set) => ({
            value: set.Id,
            label: set.DisplayName || set.Id
        }));
        // Ensure current value is representable even before sets load
        if (value && !sets.find(s => s.Id === value)) {
            options.unshift({ value, label: selectedLabel || value });
        }
        return options;
    }, [sets, value, selectedLabel]);

    return (
        <Box>
            <Group gap="xs" align="flex-end">
                <Select
                    flex={1}
                    label={label}
                    placeholder="Select a set..."
                    data={selectData}
                    value={value}
                    onChange={(val) => onChange(val || null)}
                    required={required}
                    disabled={loading || disabled}
                    error={error}
                    searchable
                    clearable={allowClear}
                    rightSection={loading ? <Loader size={16} /> : undefined}
                />
                <ActionIcon
                    variant="light"
                    onClick={() => setBrowserOpen(true)}
                    disabled={disabled}
                    mb={4}
                >
                    <IconSearch size={16} />
                </ActionIcon>
            </Group>

            {description && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {description}
                </Text>
            )}

            {errorState && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} mt="xs">
                    {errorState}
                </Alert>
            )}

            {helperText && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {helperText}
                </Text>
            )}

            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                entityType="Set"
                title="Select Set"
                callerName="SetSelector"
                singleSelection={true}
                filterFunction={browserFilterFunction}
            />
        </Box>
    );
};
